import type { FmMediaAsset } from './mediaRegistry';

// The app serves these derivatives from public/media/context/v1. The design-system
// library build (vite.ds.config.ts) has no public/ directory to serve, so it sets
// VITE_CONTEXT_MEDIA_BASE_URL to the same files in the public object store
// (lehti-media, forest-context/v1/).
const CONTEXT_MEDIA_BASE_URL: string =
  import.meta.env.VITE_CONTEXT_MEDIA_BASE_URL ?? '/media/context/v1';

const contextMediaUrl = (name: string, width: 640 | 1280): string =>
  `${CONTEXT_MEDIA_BASE_URL}/${name}-${width}.webp`;

type ContextMediaInput = Pick<
  FmMediaAsset,
  'key' | 'alt' | 'semanticContext'
> & {
  small: string;
  large: string;
  creator: string;
  sourceUrl: string;
  checksum: string;
  aspect?: FmMediaAsset['aspect'];
  focalPoint?: FmMediaAsset['focalPoint'];
  dominantTone?: FmMediaAsset['dominantTone'];
};

const licensedForestImage = ({
  key,
  small,
  large,
  alt,
  creator,
  sourceUrl,
  checksum,
  aspect = 'wide',
  focalPoint = 'center',
  dominantTone = 'green',
  semanticContext,
}: ContextMediaInput): FmMediaAsset => ({
  key,
  kind: 'image',
  src: large,
  srcSet: `${small} 640w, ${large} 1280w`,
  alt,
  aspect,
  focalPoint,
  dominantTone,
  requiresCredit: false,
  provenance: {
    source: 'licensed-stock',
    createdAt: '2026-07-13T00:00:00Z',
    creator,
    sourceUrl,
    licenseUrl: 'https://www.pexels.com/license/',
    retrievedAt: '2026-07-13T00:00:00Z',
    licenseVersion: 'Pexels License accessed 2026-07-13',
    replacementPolicy: 'stable',
  },
  webglPolicy: { webglAllowed: false },
  cropProfiles: {
    background: { aspect: 'wide', focalPoint, note: 'Safe full-bleed editorial crop' },
    split: { aspect: 'wide', focalPoint, note: 'Safe split-story crop' },
    card: { aspect: 'square', focalPoint, note: 'Safe editorial card crop' },
    'entity-card': {
      aspect: 'wide',
      focalPoint,
      note: 'Decision-card crop preserves context without dominating the record',
    },
    mobile: { aspect: 'portrait', focalPoint, note: 'Safe narrow-screen crop' },
  },
  delivery: {
    catalogVersion: 'context-forest-v1',
    objectKey: `forest-context/v1/${key}`,
    checksum,
  },
  semanticContext,
});

export const CONTEXT_MEDIA_REGISTRY: Record<string, FmMediaAsset> = {
  'forest.species.pine.overview': licensedForestImage({
    key: 'forest.species.pine.overview',
    small: contextMediaUrl('pine', 640),
    large: contextMediaUrl('pine', 1280),
    checksum: '81f65d7e7a3921d2364a4e95ad8a9dc85b86d571a7b8d4e21b7ef8b5c712db38',
    alt: 'Illustrative sunlit pine forest with an open forest floor',
    creator: 'Lauri Poldre',
    sourceUrl:
      'https://www.pexels.com/photo/tranquil-forest-scene-with-sunlit-trees-37214042/',
    focalPoint: '50% 48%',
    dominantTone: 'warm',
    semanticContext: { species: 'pine', illustrative: true },
  }),
  'forest.species.spruce.overview': licensedForestImage({
    key: 'forest.species.spruce.overview',
    small: contextMediaUrl('spruce', 640),
    large: contextMediaUrl('spruce', 1280),
    checksum: '63821a0bce7ca0ff545ba2d69c7a410c5e72fdb62184181a4e3c8f20c95434b6',
    alt: 'Illustrative dense spruce woodland with a narrow forest path',
    creator: 'Lauri Poldre',
    sourceUrl:
      'https://www.pexels.com/photo/tranquil-forest-path-through-evergreen-woodland-34309636/',
    focalPoint: '50% 62%',
    semanticContext: { species: 'spruce', illustrative: true },
  }),
  'forest.species.birch.overview': licensedForestImage({
    key: 'forest.species.birch.overview',
    small: contextMediaUrl('birch', 640),
    large: contextMediaUrl('birch', 1280),
    checksum: 'db0a3ecee40d1b5082db55ca50e50b656d9f48b80c79383d6df2affeed03c2c3',
    alt: 'Illustrative birch-dominant stand in autumn',
    creator: 'Yuliia Osadcha',
    sourceUrl:
      'https://www.pexels.com/photo/forest-during-fall-season-14186550/',
    aspect: 'portrait',
    focalPoint: '50% 48%',
    dominantTone: 'warm',
    semanticContext: { species: 'birch', illustrative: true },
  }),
  'forest.species.deciduous.overview': licensedForestImage({
    key: 'forest.species.deciduous.overview',
    small: contextMediaUrl('deciduous', 640),
    large: contextMediaUrl('deciduous', 1280),
    checksum: '070ac29fd23b2373b61b88ae7bf50a22ac199ed1fd991f6163ead1251342a191',
    alt: 'Illustrative deciduous birch woodland in late autumn',
    creator: 'Roman Biernacki',
    sourceUrl:
      'https://www.pexels.com/photo/a-path-through-a-forest-with-trees-and-leaves-19824339/',
    dominantTone: 'warm',
    semanticContext: { species: 'deciduous', illustrative: true },
  }),
  'forest.species.mixed.overview': licensedForestImage({
    key: 'forest.species.mixed.overview',
    small: contextMediaUrl('mixed-conifer', 640),
    large: contextMediaUrl('mixed-conifer', 1280),
    checksum: 'ea609b81b77f8a089a5bb8dc433f3d99b2769f365eb7dfc6b7a95c0c257d8212',
    alt: 'Illustrative mixed conifer stand with pine overstory and spruce regeneration',
    creator: 'Lauri Poldre',
    sourceUrl:
      'https://www.pexels.com/photo/lush-green-boreal-forest-with-evergreen-trees-37366042/',
    semanticContext: { species: 'mixed', illustrative: true },
  }),
  'forest.development.a0.regeneration': licensedForestImage({
    key: 'forest.development.a0.regeneration',
    small: contextMediaUrl('regeneration', 640),
    large: contextMediaUrl('regeneration', 1280),
    checksum: '23505f1e88933c8053e7bf26c8912d4da33b541e89872b01cbfe8ec7ccd64d39',
    alt: 'Illustrative planted conifer seedling on a regeneration site',
    creator: 'Lauri Poldre',
    sourceUrl: 'https://www.pexels.com/photo/33506883/',
    aspect: 'portrait',
    focalPoint: '50% 68%',
    dominantTone: 'dark',
    semanticContext: { developmentClass: 'A0', illustrative: true },
  }),
  'forest.development.t1.established-seedling': licensedForestImage({
    key: 'forest.development.t1.established-seedling',
    small: contextMediaUrl('regeneration', 640),
    large: contextMediaUrl('regeneration', 1280),
    checksum: '23505f1e88933c8053e7bf26c8912d4da33b541e89872b01cbfe8ec7ccd64d39',
    alt: 'Illustrative established young conifer seedling',
    creator: 'Lauri Poldre',
    sourceUrl: 'https://www.pexels.com/photo/33506883/',
    aspect: 'portrait',
    focalPoint: '50% 68%',
    dominantTone: 'dark',
    semanticContext: { developmentClass: 'T1', illustrative: true },
  }),
  'forest.development.t2.spruce.detail': licensedForestImage({
    key: 'forest.development.t2.spruce.detail',
    small: contextMediaUrl('t2-spruce-detail', 640),
    large: contextMediaUrl('t2-spruce-detail', 1280),
    checksum: '81ae358858d7ce80e2cc29145c65af008d227aaa9061b3c104f90d019110d30c',
    alt: 'Illustrative close view of young spruce growth',
    creator: 'neslihan',
    sourceUrl:
      'https://www.pexels.com/photo/a-close-up-of-a-small-evergreen-tree-28139657/',
    aspect: 'portrait',
    focalPoint: '50% 54%',
    semanticContext: {
      species: 'spruce',
      developmentClass: 'T2',
      illustrative: true,
    },
  }),
  'forest.structure.pine-overstory-spruce-regeneration': licensedForestImage({
    key: 'forest.structure.pine-overstory-spruce-regeneration',
    small: contextMediaUrl('mixed-conifer', 640),
    large: contextMediaUrl('mixed-conifer', 1280),
    checksum: 'ea609b81b77f8a089a5bb8dc433f3d99b2769f365eb7dfc6b7a95c0c257d8212',
    alt: 'Illustrative pine overstory with spruce regeneration beneath it',
    creator: 'Lauri Poldre',
    sourceUrl:
      'https://www.pexels.com/photo/lush-green-boreal-forest-with-evergreen-trees-37366042/',
    semanticContext: {
      structure: 'pine-overstory-spruce-regeneration',
      illustrative: true,
    },
  }),
  'forest.structure.birch-overstory-spruce-regeneration': licensedForestImage({
    key: 'forest.structure.birch-overstory-spruce-regeneration',
    small: contextMediaUrl('birch-spruce-structure', 640),
    large: contextMediaUrl('birch-spruce-structure', 1280),
    checksum: '0369c98c8c56fcee2056ba2a288f06cc66938f73c9810c76f065f0e41b5d04fa',
    alt: 'Illustrative birch overstory with conifer regeneration visible below',
    creator: 'Paul Gourmaud',
    sourceUrl: 'https://www.pexels.com/photo/photo-of-birch-trees-13444276/',
    focalPoint: '36% 56%',
    semanticContext: {
      structure: 'birch-overstory-spruce-regeneration',
      illustrative: true,
    },
  }),
  'forest.land.nonproductive-peatland': licensedForestImage({
    key: 'forest.land.nonproductive-peatland',
    small: contextMediaUrl('nonproductive-peatland', 640),
    large: contextMediaUrl('nonproductive-peatland', 1280),
    checksum: 'aa5b4880a7a2fe5ed0b57f5d66e3ac1e870cdab9654149343ed9b60335a19cf3',
    alt: 'Illustrative open peatland with sparse low pine growth',
    creator: 'Lauri Poldre',
    sourceUrl: 'https://www.pexels.com/photo/31617699/',
    focalPoint: '38% 54%',
    dominantTone: 'warm',
    semanticContext: {
      landClass: 'nonproductive-peatland',
      illustrative: true,
    },
  }),
  'forest.condition.conifer-mortality': licensedForestImage({
    key: 'forest.condition.conifer-mortality',
    small: contextMediaUrl('conifer-mortality', 640),
    large: contextMediaUrl('conifer-mortality', 1280),
    checksum: '448e31392cc8330e023491ef8c712ab3a6a88c38a3a71bfab48f87922bb6ce3c',
    alt: 'Illustrative conifer stand with visibly dead and declining crowns; cause unknown',
    creator: 'Barnabas Davoti',
    sourceUrl:
      'https://www.pexels.com/photo/trees-on-snow-covered-ground-8963290/',
    aspect: 'portrait',
    focalPoint: '48% 58%',
    dominantTone: 'cool',
    semanticContext: { condition: 'conifer-mortality', illustrative: true },
  }),
  'forest.condition.broken-trees': licensedForestImage({
    key: 'forest.condition.broken-trees',
    small: contextMediaUrl('broken-trees', 640),
    large: contextMediaUrl('broken-trees', 1280),
    checksum: '620de4d20adb68124523a40323c94c9634275d6775669fc8de858fff6fa41078',
    alt: 'Illustrative broken and fallen conifer stems; cause unknown',
    creator: 'sergeispas',
    sourceUrl:
      'https://www.pexels.com/photo/fallen-tree-in-the-forest-18554919/',
    focalPoint: '36% 48%',
    dominantTone: 'warm',
    semanticContext: { condition: 'broken-trees', illustrative: true },
  }),
};
